import styled from 'styled-components';
import { mq } from '../../theme/mediaQueries';

type Accent = 'green' | 'blue' | 'red' | 'purple' | 'teal';

const ACCENT_BG: Record<Accent, string> = {
  green: 'var(--success-soft, #E7F0E1)',
  blue: 'var(--info-soft, #E6ECFB)',
  red: 'var(--danger-soft, #FBE7E7)',
  purple: 'rgba(139, 92, 214, 0.12)',
  teal: 'rgba(13, 148, 136, 0.12)',
};
const ACCENT_FG: Record<Accent, string> = {
  green: 'var(--success-ink, var(--brand-green))',
  blue: 'var(--info-ink, #4A78D6)',
  red: 'var(--danger-ink, var(--danger))',
  purple: '#8B5CD6',
  teal: '#0D9488',
};

/* ── Report-type cards ─────────────────────────────────────── */

export const StyledTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--s-4, 16px);
  margin-bottom: var(--s-7, 28px);

  ${mq.ltLg} {
    grid-template-columns: repeat(2, 1fr);
  }
  ${mq.ltSm} {
    grid-template-columns: 1fr;
  }
`;

export const StyledTypeCard = styled.button<{ $selected?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--s-2, 8px);
  text-align: start;
  padding: var(--s-5, 20px);
  border: ${({ $selected }) =>
    $selected ? '2px solid var(--brand-green)' : '1px solid var(--line)'};
  border-radius: var(--r-lg, 14px);
  background: var(--surface);
  box-shadow: ${({ $selected }) =>
    $selected ? '0 0 0 3px rgba(31, 91, 63, 0.12), var(--sh-1)' : 'var(--sh-1)'};
  cursor: pointer;
  transition: box-shadow var(--dur-base, 180ms) var(--ease-out),
    transform var(--dur-base, 180ms) var(--ease-out),
    border-color var(--dur-base, 180ms) var(--ease-out);

  &:hover:not(:disabled) {
    box-shadow: var(--sh-2);
    transform: translateY(-2px);
  }
  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
`;

export const StyledTypeIcon = styled.span<{ $accent: Accent }>`
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: var(--r-md, 10px);
  margin-bottom: var(--s-2, 8px);
  background: ${({ $accent }) => ACCENT_BG[$accent]};
  color: ${({ $accent }) => ACCENT_FG[$accent]};

  svg {
    width: 22px;
    height: 22px;
  }
`;

export const StyledTypeTitle = styled.div`
  font-size: var(--t-md, 16px);
  font-weight: 700;
  color: var(--ink);
`;

export const StyledTypeDesc = styled.div`
  font-size: var(--t-sm, 14px);
  color: var(--muted);
  line-height: 1.45;
`;

/* ── Recent reports ────────────────────────────────────────── */

export const StyledSectionTitle = styled.h2`
  display: flex;
  align-items: baseline;
  gap: var(--s-3, 12px);
  margin: 0 0 var(--s-4, 16px);
  font-size: var(--t-lg, 18px);
  font-weight: 700;
  color: var(--ink);
`;

export const StyledSectionHint = styled.span`
  font-size: var(--t-sm, 14px);
  font-weight: 500;
  color: var(--muted);
`;

export const StyledReportList = styled.div`
  border: 1px solid var(--line);
  border-radius: var(--r-lg, 14px);
  background: var(--surface);
  box-shadow: var(--sh-1);
  overflow: hidden;
`;

export const StyledReportRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s-4, 16px);
  padding: var(--s-4, 16px) var(--s-5, 20px);

  &:not(:last-child) {
    border-bottom: 1px solid var(--line);
  }

  ${mq.ltMd} {
    flex-wrap: wrap;
  }
`;

export const StyledReportIcon = styled.span<{ $accent: Accent }>`
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  flex: none;
  border-radius: var(--r-md, 10px);
  background: ${({ $accent }) => ACCENT_BG[$accent]};
  color: ${({ $accent }) => ACCENT_FG[$accent]};

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const StyledReportBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const StyledReportPrimary = styled.div`
  font-size: var(--t-md, 15px);
  font-weight: 700;
  color: var(--ink);
`;

export const StyledReportMeta = styled.div`
  margin-top: 2px;
  font-size: var(--t-sm, 14px);
  color: var(--muted);
`;

export const StyledReportRight = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s-4, 16px);

  ${mq.ltMd} {
    width: 100%;
    justify-content: flex-end;
  }
`;

export const StyledStatusBadge = styled.span<{
  $status: 'ready' | 'failed' | 'expired' | 'processing';
}>`
  display: inline-block;
  padding: 3px 12px;
  border-radius: var(--r-full, 9999px);
  font-size: var(--t-xs, 12px);
  font-weight: 700;
  background: ${({ $status }) =>
    $status === 'ready'
      ? 'var(--success-soft, #E7F0E1)'
      : $status === 'failed'
      ? 'var(--danger-soft, #FBE7E7)'
      : $status === 'processing'
      ? 'var(--info-soft, #E6EEF7)'
      : 'var(--paper-2, #F2F0EA)'};
  color: ${({ $status }) =>
    $status === 'ready'
      ? 'var(--success-ink, var(--brand-green))'
      : $status === 'failed'
      ? 'var(--danger-ink, var(--danger))'
      : $status === 'processing'
      ? 'var(--info-ink, #2A5C9A)'
      : 'var(--muted)'};
`;

export const StyledExpiryText = styled.span`
  font-size: var(--t-sm, 14px);
  color: var(--muted);
  white-space: nowrap;
`;

export const StyledDismiss = styled.button`
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  flex: none;
  border: none;
  border-radius: var(--r-sm, 6px);
  background: transparent;
  color: var(--muted);
  cursor: pointer;

  &:hover {
    background: var(--paper-2, #f2f0ea);
    color: var(--ink);
  }
  svg {
    width: 14px;
    height: 14px;
  }
`;

export const StyledEmpty = styled.div`
  padding: var(--s-7, 28px);
  border: 1px dashed var(--line);
  border-radius: var(--r-lg, 14px);
  text-align: center;
  font-size: var(--t-sm, 14px);
  color: var(--muted);
`;

/* ── Configure panel ───────────────────────────────────────── */

export const StyledConfigPanel = styled.div`
  padding: var(--s-6, 24px);
  margin-bottom: var(--s-7, 28px);
  border: 1px solid var(--line);
  border-radius: var(--r-lg, 14px);
  background: var(--surface);
  box-shadow: var(--sh-1);
`;

export const StyledConfigHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s-4, 16px);
  margin-bottom: var(--s-5, 20px);
`;

export const StyledConfigTitle = styled.h2`
  margin: 0;
  font-size: var(--t-lg, 18px);
  font-weight: 700;
  color: var(--ink);
`;

export const StyledConfigClose = styled.button`
  border: none;
  background: transparent;
  color: var(--muted);
  font-size: var(--t-sm, 14px);
  font-weight: 600;
  cursor: pointer;
  padding: var(--s-1, 4px) var(--s-2, 8px);
  border-radius: var(--r-sm, 6px);

  &:hover {
    color: var(--ink);
    background: var(--paper-2, #f2f0ea);
  }
`;

export const StyledFieldLabel = styled.div`
  font-size: var(--t-xs, 12px);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: var(--s-3, 12px);
`;

export const StyledRangeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--s-3, 12px);
  margin-bottom: var(--s-6, 24px);
`;

export const StyledRangePill = styled.button<{ $active?: boolean }>`
  padding: 9px 20px;
  border-radius: var(--r-full, 9999px);
  font-size: var(--t-sm, 14px);
  font-weight: 600;
  cursor: pointer;
  transition: background var(--dur-base, 180ms) var(--ease-out),
    color var(--dur-base, 180ms) var(--ease-out),
    border-color var(--dur-base, 180ms) var(--ease-out);
  border: 1px solid ${({ $active }) => ($active ? 'var(--brand-green)' : 'var(--line)')};
  background: ${({ $active }) => ($active ? 'var(--brand-green)' : 'var(--surface)')};
  color: ${({ $active }) => ($active ? '#fff' : 'var(--ink)')};

  &:hover {
    border-color: var(--brand-green);
  }
`;

export const StyledColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--s-3, 12px);

  ${mq.ltLg} {
    grid-template-columns: repeat(2, 1fr);
  }
  ${mq.ltSm} {
    grid-template-columns: 1fr;
  }
`;

export const StyledColumnCheck = styled.button<{ $checked?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--s-3, 12px);
  text-align: start;
  padding: var(--s-3, 12px) var(--s-4, 16px);
  border-radius: var(--r-md, 10px);
  cursor: pointer;
  transition: background var(--dur-base, 180ms) var(--ease-out),
    border-color var(--dur-base, 180ms) var(--ease-out);
  border: 1px solid ${({ $checked }) => ($checked ? 'transparent' : 'var(--line)')};
  background: ${({ $checked }) =>
    $checked ? 'var(--success-soft, #E7F0E1)' : 'var(--surface)'};

  &:hover {
    border-color: var(--brand-green);
  }
`;

export const StyledCheckbox = styled.span<{ $checked?: boolean }>`
  position: relative;
  display: inline-block;
  width: 20px;
  height: 20px;
  flex: none;
  border-radius: var(--r-sm, 6px);
  border: 1px solid ${({ $checked }) => ($checked ? 'var(--brand-green)' : 'var(--line)')};
  background: ${({ $checked }) => ($checked ? 'var(--brand-green)' : 'var(--surface)')};

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 6.5px;
    width: 4px;
    height: 9px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    opacity: ${({ $checked }) => ($checked ? 1 : 0)};
  }
`;

export const StyledColumnLabel = styled.span`
  font-size: var(--t-sm, 14px);
  color: var(--ink);
`;

export const StyledConfigFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s-4, 16px);
  margin-top: var(--s-6, 24px);
  padding-top: var(--s-5, 20px);
  border-top: 1px solid var(--line);

  ${mq.ltSm} {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const StyledConfigHint = styled.span`
  font-size: var(--t-sm, 14px);
  color: var(--muted);
`;

export const StyledCustomRange = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--s-4, 16px);
  margin: calc(-1 * var(--s-2, 8px)) 0 var(--s-6, 24px);
`;

export const StyledDateField = styled.label`
  display: flex;
  flex-direction: column;
  gap: var(--s-2, 8px);
  font-size: var(--t-xs, 12px);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
`;

export const StyledDateInput = styled.input`
  padding: 9px 14px;
  min-width: 170px;
  border: 1px solid var(--line);
  border-radius: var(--r-md, 10px);
  background: var(--surface);
  color: var(--ink);
  font-family: inherit;
  font-size: var(--t-sm, 14px);
  text-transform: none;
  letter-spacing: normal;

  &:focus {
    outline: none;
    border-color: var(--brand-green);
    box-shadow: 0 0 0 3px rgba(31, 91, 63, 0.12);
  }
`;
